import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@tsin-env/environment';
import {
  CreateApplicationInput,
  CreateRefenceInput,
  IApplicationDTO,
  IApplicationNoteStep,
  IPoolInput,
  IReferences,
  IUploadAvatar,
  IUploadCoverLatter,
  IUploadDocs,
  IUserApplicationState,
  UpdateApplicationInput,
  UpdateReferenceInput,
} from './application.interface';
import { Observable } from 'rxjs';
import {
  IEmailHistoryItem,
  IHiringWorkflowTemplateResponse,
  IPreviewEmailResponse,
  ISendEmailResponse,
  ISendHiringWorkflowEmailInput
} from '../../../pages/management/hiring-workflows/hiring-workflows.interface';

const API_URI = `${environment.apiBaseUrl}/applications`;

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  http: HttpClient = inject(HttpClient);

  getApplications(queryParams?: { [key: string]: any }) {
    let params = new HttpParams();

    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        const value = queryParams[key];
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }

    return this.http.get(API_URI, { params });
  }

  getUserApplications(id: string) {
    return this.http.get(API_URI + '/user-applications/' + id);
  }

  createApplication(input: CreateApplicationInput) {
    return this.http.post(API_URI, input);
  }

  applicationNote(input: IApplicationNoteStep): Observable<IApplicationDTO> {
    return this.http.patch<IApplicationDTO>(API_URI + `/application-note`, input);
  }

  referenceNote(input: { referenceId: string, note: string }): Observable<IReferences> {
    return this.http.patch<IReferences>(API_URI + `/reference-note`, input);
  }

  getSingle(id: string) {
    return this.http.get(API_URI + `/single/${id}`);
  }

  updateApplication(input: UpdateApplicationInput) {
    return this.http.post(API_URI, input);
  }

  delete(id: string) {
    return this.http.delete(API_URI + `/single/${id}`);
  }

  reviewStatus(input: { id: string; applicationReviewStatus: string }) {
    return this.http.patch(API_URI + '/application-review-status', input);
  }

  sendBulkStatusEmails(input: {ids: string[]}){
    return this.http.post(API_URI + '/send-bulk-status-emails', input);
  }

  submitApplication(input: { id: string }) {
    return this.http.patch(API_URI + '/submit', input);
  }

  uploadAvatar(input: IUploadAvatar) {
    return this.http.post(API_URI + '/upload-avatar', input);
  }

  uploadResume(input: IUploadDocs) {
    return this.http.post(API_URI + '/upload-resume', input);
  }

  uploadCoverLetter(input: IUploadCoverLatter) {
    return this.http.post(API_URI + '/upload-cover-letter', input);
  }

  addToPool(input: IPoolInput) {
    return this.http.patch(API_URI + '/add-to-pool', input);
  }

  getPoolAppl(department: string) {
    return this.http.get(API_URI + '/all/' + department);
  }

  addReference(input: CreateRefenceInput) {
    return this.http.post(API_URI + '/references', input);
  }

  updateReference(input: UpdateReferenceInput) {
    return this.http.put(API_URI + '/references', input);
  }

  getApplicationReference(id: string) {
    return this.http.get(API_URI + `/references/${id}`);
  }

  deleteReference(id: string) {
    return this.http.delete(API_URI + `/references/${id}`);
  }

  sendHiringWorkflowEmail(input: ISendHiringWorkflowEmailInput): Observable<ISendEmailResponse> {
    return this.http.post<ISendEmailResponse>(API_URI + '/send-hiring-workflow-email', input);
  }

  previewHiringWorkflowEmail(input: {
    candidateName: string;
    jobTitle: string;
    template: string;
    subject?: string;
    message?: string;
  }): Observable<IPreviewEmailResponse> {
    return this.http.post<IPreviewEmailResponse>(API_URI + '/preview-hiring-workflow-email', input);
  }

  previewHiringWorkflowEmailWithInputs(input: {
    applicationId: string;
    template: string;
    inputs: any;
    fullPreview?: boolean;
  }): Observable<{ subject: string; message: string }> {
    return this.http.post<{ subject: string; message: string }>(API_URI + '/preview-hiring-workflow-email-with-inputs', input);
  }

  getApplicationEmailHistory(id: string): Observable<IEmailHistoryItem[]> {
    return this.http.get<IEmailHistoryItem[]>(API_URI + `/email-history/${id}`);
  }

  getHiringWorkflowTemplate(template: string, jobTitle: string): Observable<IHiringWorkflowTemplateResponse> {
    return this.http.get<IHiringWorkflowTemplateResponse>(API_URI + `/hiring-workflow-template/${template}/${encodeURIComponent(jobTitle)}`);
  }

  // State Machine APIs

  /**
   * Get valid transitions for an application
   */
  getValidTransitions(applicationId: string): Observable<any> {
    return this.http.get(API_URI + `/valid-transitions/${applicationId}`);
  }

  /**
   * Get valid sub-statuses for a given status
   */
  getValidSubStatuses(status: string): Observable<any> {
    return this.http.get(API_URI + `/valid-sub-statuses/${status}`);
  }

  /**
   * Transition application to a new status
   */
  transitionApplicationStatus(input: {
    id: string;
    status: string;
    subStatus?: string;
    reason?: string;
    notes?: string;
  }): Observable<any> {
    return this.http.patch(API_URI + '/transition-status', input);
  }

  /**
   * Change sub-status within the same main status
   */
  changeSubStatus(input: {
    id: string;
    subStatus: string;
    notes?: string;
  }): Observable<any> {
    return this.http.patch(API_URI + '/change-sub-status', input);
  }

  /**
   * Get candidates grouped by user with all their applications
   */
  getCandidatesGrouped(queryParams?: { [key: string]: any }): Observable<any> {
    let params = new HttpParams();

    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        const value = queryParams[key];
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }

    return this.http.get(API_URI + '/candidates-grouped', { params });
  }

  /**
   * Add candidate to pool (all applications)
   */
  addCandidateToPool(input: { candidateId: string; pool: any[] }): Observable<any> {
    return this.http.patch(API_URI + '/add-candidate-to-pool', input);
  }

  /**
   * Send interview invitation email
   */
  sendInterviewInvitation(payload: any): Observable<any> {
    return this.http.post(API_URI + '/send-interview-invitation', payload);
  }

  /**
   * Preview interview invitation email
   */
  previewInterviewInvitation(payload: any): Observable<{ subject: string; html: string }> {
    return this.http.post<{ subject: string; html: string }>(
      API_URI + '/preview-interview-invitation',
      payload
    );
  }

  /**
   * Cancel application
   */
  cancelApplication(id: string): Observable<any> {
    return this.http.post(API_URI + `/cancel-application/${id}`, {});
  }

  /**
   * Update application status
   */
  updateApplicationStatus(input: { id: string; applicationStatus: string }): Observable<IApplicationDTO> {
    return this.http.patch<IApplicationDTO>(API_URI + '/application-status', input);
  }
}
